import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { 
  Home, LogOut, FileText, CheckCircle, Clock, AlertCircle, 
  Phone, Mail, Calendar, DollarSign, Hammer, Shield, Camera,
  Download, MessageSquare, ClipboardList, Truck, HardHat,
  StickyNote, Image, Bell, Send, Plus, Trash2, User
} from 'lucide-react';
import CompanyLogo from '../components/CompanyLogo';
import LanguageToggle from '../components/LanguageToggle';
import { useLanguage } from '../i18n/LanguageContext';
import { COMPANY as DEFAULT_COMPANY } from '../config/company';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const HomeownerDashboard = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [companySettings, setCompanySettings] = useState(DEFAULT_COMPANY);
  
  // Notes state
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  
  // Messages state
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const messagesEndRef = useRef(null);
  
  // Notifications state
  const [notifications, setNotifications] = useState([]);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [notificationType, setNotificationType] = useState('email');

  // Fetch company settings
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/settings`);
        if (response.ok) {
          const data = await response.json();
          setCompanySettings({
            ...DEFAULT_COMPANY,
            name: data.name || DEFAULT_COMPANY.name,
            phone: data.phone || DEFAULT_COMPANY.phone,
            email: data.email || DEFAULT_COMPANY.email,
            address: data.address || DEFAULT_COMPANY.address,
            city: data.city || DEFAULT_COMPANY.city,
            state: data.state || DEFAULT_COMPANY.state,
            zip: data.zip || DEFAULT_COMPANY.zip,
            primaryColor: data.primaryColor || DEFAULT_COMPANY.primaryColor,
          });
        }
      } catch (error) {
        console.error('Error fetching settings:', error);
      }
    };
    fetchSettings();
  }, []);

  // Helper to normalize project data from estimates
  const normalizeProject = (estimate) => {
    return {
      ...estimate,
      total_amount: estimate.grand_total || 0,
      paid_amount: estimate.paid_amount || 0,
      construction_progress: estimate.construction_progress || {},
      construction_dates: estimate.construction_dates || {},
      next_milestone: estimate.next_milestone || '',
      expected_start_date: estimate.expected_start_date || '',
      photos: estimate.photos || [],
    };
  };

  // Check if user is admin
  const isAdmin = user?.email === 'info@jymegafcconstruction.net';
  const [homeowners, setHomeowners] = useState([]);

  useEffect(() => {
    const token = localStorage.getItem('homeownerToken');
    const userData = localStorage.getItem('homeownerUser');
    
    if (!token || !userData) {
      navigate('/homeowner/login');
      return;
    }
    
    const parsedUser = JSON.parse(userData);
    setUser(parsedUser);
    
    // Check if admin
    if (parsedUser.email === 'info@jymegafcconstruction.net') {
      fetchAllProjects();
      fetchHomeowners();
    } else {
      fetchProjects(parsedUser.id);
    }
  }, [navigate]);

  const fetchAllProjects = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/estimates`);
      if (response.ok) {
        const data = await response.json();
        const normalizedProjects = data.map(normalizeProject);
        setProjects(normalizedProjects);
        if (normalizedProjects.length > 0) {
          setSelectedProject(normalizedProjects[0]);
          fetchProjectData(normalizedProjects[0].id);
        }
      }
    } catch (error) {
      console.error('Error fetching all projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchHomeowners = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/homeowners`);
      if (response.ok) {
        setHomeowners(await response.json());
      }
    } catch (error) {
      console.error('Error fetching homeowners:', error);
    }
  };

  // Check if a project's client has registered
  const getRegisteredHomeowner = (project) => {
    return homeowners.find(h => 
      h.email?.toLowerCase() === project.client_email?.toLowerCase() ||
      h.name?.toLowerCase() === project.client_name?.toLowerCase()
    );
  };

  const fetchProjects = async (homeownerId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/homeowner/${homeownerId}/projects`);
      if (response.ok) {
        const data = await response.json();
        if (data.length > 0) {
          // Normalize the projects
          const normalizedProjects = data.map(normalizeProject);
          setProjects(normalizedProjects);
          setSelectedProject(normalizedProjects[0]);
          fetchProjectData(normalizedProjects[0].id);
        } else {
          // No projects found
          setProjects([]);
          setSelectedProject(null);
        }
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
      setProjects([]);
      setSelectedProject(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchProjectData = async (projectId) => {
    try {
      // Fetch notes
      const notesRes = await fetch(`${BACKEND_URL}/api/homeowner/notes/${projectId}`);
      if (notesRes.ok) setNotes(await notesRes.json());
      
      // Fetch messages
      const msgsRes = await fetch(`${BACKEND_URL}/api/homeowner/messages/${projectId}`);
      if (msgsRes.ok) setMessages(await msgsRes.json());
      
      // Fetch notifications
      const notifsRes = await fetch(`${BACKEND_URL}/api/homeowner/notifications/${projectId}`);
      if (notifsRes.ok) setNotifications(await notifsRes.json());
    } catch (error) {
      console.error('Error fetching project data:', error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('homeownerToken');
    localStorage.removeItem('homeownerUser');
    toast.success(language === 'es' ? 'Sesión cerrada' : 'Logged out');
    navigate('/homeowner/login');
  };

  // Notes functions
  const addNote = async () => {
    if (!newNote.trim()) return;
    try {
      const response = await fetch(`${BACKEND_URL}/api/homeowner/notes?homeowner_id=${user?.id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: newNote, project_id: selectedProject?.id })
      });
      if (response.ok) {
        const note = await response.json();
        setNotes([note, ...notes]);
        setNewNote('');
        toast.success(language === 'es' ? 'Nota agregada' : 'Note added');
      }
    } catch (error) {
      console.error('Error adding note:', error);
    }
  };

  const deleteNote = async (noteId) => {
    try {
      await fetch(`${BACKEND_URL}/api/homeowner/notes/${noteId}`, { method: 'DELETE' });
      setNotes(notes.filter(n => n.id !== noteId));
      toast.success(language === 'es' ? 'Nota eliminada' : 'Note deleted');
    } catch (error) {
      console.error('Error deleting note:', error);
    }
  };

  // Messages functions
  const sendMessage = async () => {
    if (!newMessage.trim()) return;
    try {
      const response = await fetch(`${BACKEND_URL}/api/homeowner/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          content: newMessage, 
          project_id: selectedProject?.id,
          sender_type: 'homeowner'
        })
      });
      if (response.ok) {
        const msg = await response.json();
        setMessages([...messages, msg]);
        setNewMessage('');
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  // Request notification
  const requestNotification = async () => {
    if (!notificationMessage.trim()) return;
    try {
      const response = await fetch(`${BACKEND_URL}/api/homeowner/notifications`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          project_id: selectedProject?.id,
          message: notificationMessage,
          notification_type: notificationType
        })
      });
      if (response.ok) {
        const data = await response.json();
        setNotifications([data.notification, ...notifications]);
        setNotificationMessage('');
        toast.success(language === 'es' ? 'Notificación solicitada' : 'Notification requested');
      }
    } catch (error) {
      console.error('Error requesting notification:', error);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      new_lead: { label: 'New', label_es: 'Nuevo', color: 'bg-blue-100 text-blue-800' },
      estimate_provided: { label: 'Estimate Ready', label_es: 'Presupuesto Listo', color: 'bg-purple-100 text-purple-800' },
      contract_signed: { label: 'In Progress', label_es: 'En Progreso', color: 'bg-amber-100 text-amber-800' },
      work_completed: { label: 'Completed', label_es: 'Completado', color: 'bg-green-100 text-green-800' },
    };
    const config = statusConfig[status] || statusConfig.new_lead;
    return <Badge className={config.color}>{language === 'es' ? config.label_es : config.label}</Badge>;
  };

  const calculateProgress = (project) => {
    if (!project?.construction_progress || Object.keys(project.construction_progress).length === 0) return 0;
    const steps = Object.values(project.construction_progress);
    const completed = steps.filter(Boolean).length;
    return Math.round((completed / steps.length) * 100);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  const progressSteps = [
    { key: 'contract_signed', label: 'Contract Signed', label_es: 'Contrato Firmado', icon: FileText },
    { key: 'permit_applied', label: 'Permit Applied', label_es: 'Permiso Solicitado', icon: ClipboardList },
    { key: 'permit_granted', label: 'Permit Granted', label_es: 'Permiso Otorgado', icon: CheckCircle },
    { key: 'hoa_filed', label: 'HOA Filed', label_es: 'HOA Presentado', icon: FileText },
    { key: 'hoa_granted', label: 'HOA Approved', label_es: 'HOA Aprobado', icon: CheckCircle },
    { key: 'materials_ordered', label: 'Materials Ordered', label_es: 'Materiales Ordenados', icon: Truck },
    { key: 'delivery_date_set', label: 'Delivery Scheduled', label_es: 'Entrega Programada', icon: Calendar },
    { key: 'materials_delivered', label: 'Materials Delivered', label_es: 'Materiales Entregados', icon: Truck },
    { key: 'demolition_begins', label: 'Work Started', label_es: 'Trabajo Iniciado', icon: HardHat },
  ];

  const tabs = [
    { id: 'overview', label: language === 'es' ? 'Resumen' : 'Project Status', icon: Home },
    { id: 'documents', label: language === 'es' ? 'Documentos' : 'Documents', icon: FileText },
    { id: 'contact', label: language === 'es' ? 'Contacto' : 'Contact Us', icon: Phone },
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: companySettings.primaryColor }}></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-amber-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
        <nav className="container mx-auto px-4 sm:px-6 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Link to="/" className="hover:opacity-80">
                <CompanyLogo className="h-10 w-auto" />
              </Link>
              <div className="hidden sm:block">
                <h1 className="text-lg font-semibold text-gray-800">
                  {language === 'es' ? 'Portal del Cliente' : 'Client Portal'}
                </h1>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <LanguageToggle />
              <span className="hidden sm:inline text-sm text-gray-600">{user?.name}</span>
              <Button variant="ghost" onClick={handleLogout} className="text-red-600 hover:text-red-700 hover:bg-red-50">
                <LogOut className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">{language === 'es' ? 'Salir' : 'Logout'}</span>
              </Button>
            </div>
          </div>
        </nav>
      </header>

      <main className="container mx-auto px-4 sm:px-6 py-6">
        {/* Welcome Banner */}
        <div className="mb-6 p-4 sm:p-6 rounded-xl text-white" style={{ backgroundColor: companySettings.primaryColor }}>
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl sm:text-2xl font-bold mb-1">
                {language === 'es' ? `¡Hola, ${user?.name}!` : `Hello, ${user?.name}!`}
                {isAdmin && (
                  <Badge className="ml-2 bg-white/20 text-white hover:bg-white/30">
                    {language === 'es' ? 'Vista Admin' : 'Admin View'}
                  </Badge>
                )}
              </h2>
              <p className="text-amber-100 text-sm sm:text-base">
                {isAdmin 
                  ? (language === 'es' ? 'Vista de administrador - Todos los proyectos' : 'Administrator view - All projects')
                  : (language === 'es' ? 'Aquí puede ver el progreso de su proyecto' : 'Here you can view your project progress')
                }
              </p>
            </div>
            <Home className="h-12 w-12 sm:h-16 sm:w-16 text-white/20" />
          </div>
        </div>

        {/* Admin Stats Bar */}
        {isAdmin && (
          <div className="mb-6 grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-blue-600">{projects.length}</p>
                <p className="text-sm text-blue-800">{language === 'es' ? 'Total Proyectos' : 'Total Projects'}</p>
              </CardContent>
            </Card>
            <Card className="bg-green-50 border-green-200">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-green-600">{homeowners.length}</p>
                <p className="text-sm text-green-800">{language === 'es' ? 'Usuarios Registrados' : 'Registered Users'}</p>
              </CardContent>
            </Card>
            <Card className="bg-amber-50 border-amber-200">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-amber-600">
                  {projects.filter(p => getRegisteredHomeowner(p)).length}
                </p>
                <p className="text-sm text-amber-800">{language === 'es' ? 'Proyectos Vinculados' : 'Linked Projects'}</p>
              </CardContent>
            </Card>
            <Card className="bg-gray-50 border-gray-200">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-gray-600">
                  {projects.filter(p => !getRegisteredHomeowner(p)).length}
                </p>
                <p className="text-sm text-gray-800">{language === 'es' ? 'Sin Registrar' : 'Unregistered'}</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tabs */}
        <div className="mb-6 flex flex-wrap gap-2 bg-white rounded-lg p-2 shadow-sm">
          {tabs.map(tab => (
            <Button
              key={tab.id}
              variant={activeTab === tab.id ? 'default' : 'ghost'}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 sm:flex-none ${activeTab === tab.id ? 'text-white' : 'text-gray-600'}`}
              style={activeTab === tab.id ? { backgroundColor: companySettings.primaryColor } : {}}
            >
              <tab.icon className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">{tab.label}</span>
            </Button>
          ))}
        </div>

        {/* Project Selector - show if admin or multiple projects */}
        {(isAdmin || projects.length > 1) && projects.length > 0 && (
          <div className="mb-6 bg-white rounded-lg p-4 shadow-sm">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {language === 'es' ? 'Seleccionar Proyecto' : 'Select Project'}
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {projects.map(p => {
                const registeredUser = isAdmin ? getRegisteredHomeowner(p) : null;
                return (
                  <div
                    key={p.id}
                    onClick={() => {
                      setSelectedProject(p);
                      fetchProjectData(p.id);
                    }}
                    className={`p-3 border rounded-lg cursor-pointer transition-all ${
                      selectedProject?.id === p.id 
                        ? 'border-amber-500 bg-amber-50 ring-2 ring-amber-500' 
                        : 'border-gray-200 hover:border-amber-300 hover:bg-amber-50/50'
                    }`}
                    data-testid={`project-card-${p.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-gray-800 truncate">{p.client_name}</p>
                        <p className="text-xs text-gray-500 truncate">{p.client_address}</p>
                        <p className="text-xs font-mono mt-1" style={{ color: companySettings.primaryColor }}>
                          {p.estimate_number}
                        </p>
                      </div>
                      {isAdmin && (
                        <div className="ml-2">
                          {registeredUser ? (
                            <Badge className="bg-green-100 text-green-700 text-xs whitespace-nowrap">
                              <User className="h-3 w-3 mr-1" />
                              {language === 'es' ? 'Registrado' : 'Registered'}
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-gray-500 text-xs whitespace-nowrap">
                              {language === 'es' ? 'Sin cuenta' : 'No account'}
                            </Badge>
                          )}
                        </div>
                      )}
                    </div>
                    {isAdmin && registeredUser && (
                      <div className="mt-2 pt-2 border-t border-gray-100 text-xs text-gray-500">
                        <p><strong>{language === 'es' ? 'Email:' : 'Email:'}</strong> {registeredUser.email}</p>
                        <p><strong>{language === 'es' ? 'Registrado:' : 'Registered:'}</strong> {new Date(registeredUser.created_at).toLocaleDateString()}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* No Projects State */}
        {projects.length === 0 && (
          <Card className="text-center py-12">
            <CardContent>
              <FileText className="h-16 w-16 mx-auto mb-4 text-gray-300" />
              <h3 className="text-xl font-semibold text-gray-700 mb-2">
                {language === 'es' ? 'No hay proyectos' : 'No Projects Found'}
              </h3>
              <p className="text-gray-500 max-w-md mx-auto">
                {language === 'es' 
                  ? 'No encontramos proyectos asociados a su cuenta. Si cree que esto es un error, por favor contáctenos.'
                  : 'We couldn\'t find any projects associated with your account. If you believe this is an error, please contact us.'}
              </p>
              <div className="mt-6 flex justify-center gap-4">
                <a href={`tel:${companySettings.phone}`} className="inline-flex items-center px-4 py-2 rounded-md text-white" style={{ backgroundColor: companySettings.primaryColor }}>
                  <Phone className="h-4 w-4 mr-2" />
                  {companySettings.phone}
                </a>
                <a href={`mailto:${companySettings.email}`} className="inline-flex items-center px-4 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50">
                  <Mail className="h-4 w-4 mr-2" />
                  {language === 'es' ? 'Enviar Email' : 'Send Email'}
                </a>
              </div>
            </CardContent>
          </Card>
        )}

        {selectedProject && (
          <>
            {/* PROJECT STATUS TAB */}
            {activeTab === 'overview' && (
              <div className="space-y-6">
                {/* Project Overview Card */}
                <Card>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="flex items-center">
                        <Hammer className="mr-2 h-5 w-5" style={{ color: companySettings.primaryColor }} />
                        {language === 'es' ? 'Estado del Proyecto' : 'Project Status'}
                      </CardTitle>
                      {getStatusBadge(selectedProject.lead_status)}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <p className="text-sm text-gray-500">{language === 'es' ? 'Dirección' : 'Address'}</p>
                        <p className="font-medium text-gray-900">{selectedProject.client_address}</p>
                      </div>
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <p className="text-sm text-gray-500">{language === 'es' ? 'Tipo de Proyecto' : 'Project Type'}</p>
                        <p className="font-medium text-gray-900 capitalize">{selectedProject.project_type?.replace(/_/g, ' ')}</p>
                      </div>
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <p className="text-sm text-gray-500">{language === 'es' ? 'Presupuesto' : 'Estimate'}</p>
                        <p className="font-medium font-mono" style={{ color: companySettings.primaryColor }}>{selectedProject.estimate_number}</p>
                      </div>
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <p className="text-sm text-gray-500">{language === 'es' ? 'Total del Proyecto' : 'Project Total'}</p>
                        <p className="font-bold text-green-600 text-lg">{formatCurrency(selectedProject.total_amount)}</p>
                      </div>
                    </div>

                    {/* Progress Bar */}
                    <div className="bg-amber-50 p-4 rounded-lg">
                      <div className="flex justify-between items-center mb-3">
                        <span className="text-sm font-medium text-gray-700">
                          {language === 'es' ? 'Progreso del Proyecto' : 'Project Progress'}
                        </span>
                        <span className="text-lg font-bold" style={{ color: companySettings.primaryColor }}>
                          {calculateProgress(selectedProject)}%
                        </span>
                      </div>
                      <Progress value={calculateProgress(selectedProject)} className="h-3" />
                      {selectedProject.next_milestone && (
                        <p className="text-sm text-amber-700 mt-3">
                          <strong>{language === 'es' ? 'Próximo paso:' : 'Next step:'}</strong> {selectedProject.next_milestone}
                        </p>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Payment Summary */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <DollarSign className="mr-2 h-5 w-5" style={{ color: companySettings.primaryColor }} />
                      {language === 'es' ? 'Resumen de Pagos' : 'Payment Summary'}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="text-center p-4 bg-gray-50 rounded-lg">
                        <p className="text-sm text-gray-500 mb-1">{language === 'es' ? 'Total del Proyecto' : 'Project Total'}</p>
                        <p className="text-2xl font-bold text-gray-900">{formatCurrency(selectedProject.total_amount)}</p>
                      </div>
                      <div className="text-center p-4 bg-green-50 rounded-lg">
                        <p className="text-sm text-gray-500 mb-1">{language === 'es' ? 'Pagado' : 'Paid'}</p>
                        <p className="text-2xl font-bold text-green-600">{formatCurrency(selectedProject.paid_amount)}</p>
                      </div>
                      <div className="text-center p-4 bg-amber-50 rounded-lg">
                        <p className="text-sm text-gray-500 mb-1">{language === 'es' ? 'Saldo Pendiente' : 'Balance Due'}</p>
                        <p className="text-2xl font-bold" style={{ color: companySettings.primaryColor }}>
                          {formatCurrency((selectedProject.total_amount || 0) - (selectedProject.paid_amount || 0))}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* DOCUMENTS TAB */}
            {activeTab === 'documents' && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <FileText className="mr-2 h-5 w-5" style={{ color: companySettings.primaryColor }} />
                    {language === 'es' ? 'Documentos del Proyecto' : 'Project Documents'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {(!selectedProject.documents || selectedProject.documents.length === 0) && 
                   (!selectedProject.photos || selectedProject.photos.length === 0) ? (
                    <div className="text-center py-12">
                      <FileText className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                      <p className="text-gray-500">{language === 'es' ? 'No hay documentos disponibles' : 'No documents available yet'}</p>
                      <p className="text-sm text-gray-400 mt-1">{language === 'es' ? 'Los documentos aparecerán aquí cuando estén disponibles' : 'Documents will appear here when available'}</p>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {/* PDF Documents */}
                      {selectedProject.documents && selectedProject.documents.length > 0 && (
                        <div>
                          <h4 className="font-medium text-gray-700 mb-3 flex items-center">
                            <FileText className="h-4 w-4 mr-2 text-red-500" />
                            {language === 'es' ? 'Documentos PDF' : 'PDF Documents'}
                          </h4>
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                            {selectedProject.documents.map((doc, i) => {
                              const docName = typeof doc === 'object' ? doc.original_filename : doc.split('/').pop().split('_').slice(1).join('_');
                              const docUrl = typeof doc === 'object' ? `${BACKEND_URL}/uploads/${doc.path}` : `${BACKEND_URL}/uploads/${doc}`;
                              return (
                                <a key={i} href={docUrl} target="_blank" rel="noreferrer" 
                                   className="flex items-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors border">
                                  <div className="w-10 h-10 bg-red-100 rounded flex items-center justify-center mr-3">
                                    <FileText className="h-5 w-5 text-red-600" />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-gray-900 truncate">{docName}</p>
                                    <p className="text-xs text-gray-500">{language === 'es' ? 'Click para ver' : 'Click to view'}</p>
                                  </div>
                                  <Download className="h-4 w-4 text-gray-400" />
                                </a>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Photos */}
                      {selectedProject.photos && selectedProject.photos.length > 0 && (
                        <div>
                          <h4 className="font-medium text-gray-700 mb-3 flex items-center">
                            <Image className="h-4 w-4 mr-2 text-blue-500" />
                            {language === 'es' ? 'Fotos del Proyecto' : 'Project Photos'}
                          </h4>
                          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                            {selectedProject.photos.map((photo, i) => {
                              const photoUrl = typeof photo === 'object' ? `${BACKEND_URL}/uploads/${photo.path}` : `${BACKEND_URL}/uploads/${photo}`;
                              return (
                                <a key={i} href={photoUrl} target="_blank" rel="noreferrer" 
                                   className="aspect-square bg-gray-100 rounded-lg overflow-hidden hover:opacity-90 transition-opacity">
                                  <img src={photoUrl} alt={`Photo ${i + 1}`} className="w-full h-full object-cover" />
                                </a>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* CONTACT US TAB */}
            {activeTab === 'contact' && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Phone className="mr-2 h-5 w-5" style={{ color: companySettings.primaryColor }} />
                    {language === 'es' ? 'Contáctenos' : 'Contact Us'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-center py-6">
                    <p className="text-gray-600 mb-6">
                      {language === 'es' 
                        ? '¿Tiene preguntas sobre su proyecto? Estamos aquí para ayudarle.' 
                        : 'Have questions about your project? We\'re here to help.'}
                    </p>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-lg mx-auto">
                      <a href={`tel:${companySettings.phone}`} 
                         className="flex items-center justify-center p-4 rounded-lg text-white transition-colors"
                         style={{ backgroundColor: companySettings.primaryColor }}>
                        <Phone className="h-5 w-5 mr-2" />
                        <span className="font-medium">{companySettings.phone}</span>
                      </a>
                      <a href={`mailto:${companySettings.email}`} 
                         className="flex items-center justify-center p-4 bg-gray-100 rounded-lg text-gray-700 hover:bg-gray-200 transition-colors">
                        <Mail className="h-5 w-5 mr-2" />
                        <span className="font-medium">{language === 'es' ? 'Enviar Email' : 'Send Email'}</span>
                      </a>
                    </div>

                    <div className="mt-8 p-6 bg-gray-50 rounded-lg max-w-md mx-auto">
                      <h4 className="font-semibold text-gray-800 mb-2">{companySettings.name}</h4>
                      <p className="text-gray-600 text-sm">{companySettings.address}</p>
                      <p className="text-gray-600 text-sm">{companySettings.city}, {companySettings.state} {companySettings.zip}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </main>

      {/* Footer */}
      <footer className="bg-white border-t mt-8 py-6">
        <div className="container mx-auto px-4 text-center text-sm text-gray-500">
          <p>{companySettings.name} • {companySettings.address}, {companySettings.city}, {companySettings.state} {companySettings.zip}</p>
          <p className="mt-1">{companySettings.phone} • {companySettings.email}</p>
        </div>
      </footer>
    </div>
  );
};

export default HomeownerDashboard;
